<template>
  <div class="cs-card-content">
    <span
      v-scrollto="{
        target: '.campsite-subsection.reviews',
        options: { duration: 200, offset: 0 },
      }"
      ><pu-rating-bubble
        v-if="campsite.rating"
        :rating="campsite.rating * 2"
        :rate-count="campsite.rateCount"
        class="campsite-rating"
    /></span>
    <campsite-next-open :campsite="campsite" />
    <h1 class="cs-name" data-test="campsite-details-name">
      {{ campsite.name }}
    </h1>
    <campsite-detail-location-row v-bind="locationRowProps" />
    <pu-nutshells
      v-if="campsite.nutshells.length"
      :nutshells="campsite.nutshells"
      :with-jump-link="true"
      :data-sctrack="`${String($route.name)}--CampsiteDetailCardContent-nutshells`"
      class="nutshells"
      @scroll-triggered="$emit('scroll-triggered', $event)"
    />
    <last-booked v-if="campsite.lastBooked" :date="campsite.lastBooked" />

    <div
      v-if="!isDated"
      class="button-wrapper"
      :class="{ 'not-available-to-search': !campsite.availableToSearch }"
    >
      <template v-if="campsite.availableToSearch">
        <pu-campsite-availability-link
          id="CheckAvailabilityPanel"
          :hierarchy-path="campsite.path"
          :slug="campsite.slug"
          :data-sctrack="`${String($route.name)}--CampsiteDetailCardContent-availability-link`"
          class="button primary asblock check-availability-btn"
          data-test="check-availability-panel"
        >
          <translate>Check availability</translate>
        </pu-campsite-availability-link>
      </template>
      <template v-else>
        <translate class="zero-availability-msg">
          No availability at present on Pitchup.com
        </translate>
        <div class="zero-availability-options">
          <pu-campsite-availability-link
            id="CheckAvailabilityPanel"
            :hierarchy-path="campsite.path"
            :slug="campsite.slug"
            :data-sctrack="`${String($route.name)}--CampsiteDetailCardContent-availability-link-zero-avail`"
            class="button primary"
          >
            <translate>Add dates</translate>
          </pu-campsite-availability-link>
          <span class="options">
            <pu-campsite-availability-link
              :hierarchy-path="campsite.path"
              :slug="campsite.slug"
            >
              <i18n-t
                keypath="{chooseYourDates} and we'll email when available."
              >
                <template #chooseYourDates>
                  <translate>Choose your dates</translate>
                </template>
              </i18n-t>
            </pu-campsite-availability-link>
            <a
              v-scrollto="{
                target: '.nearby-bookable',
                options: { duration: 200, offset: 0 },
              }"
            >
              <translate>See nearby bookable sites</translate>
            </a>
          </span>
        </div>
      </template>
    </div>
  </div>
</template>
<script lang="ts">
import { Component, Prop, Vue, toNative } from '~/utility/pu-class-decorator'
import { preloadRouteComponents } from 'nuxt/app'
import { useRouter } from 'vue-router'
import PuNutshells from '../../../components/campsite/campsite-card/components/PuNutshells.vue'
import { CampsiteDetails } from '../types'
import { CampsiteDetailCardContentProps } from './CampsiteDetailCardContentProps'
import CampsiteDetailLocationRow from './CampsiteDetailLocationRow.vue'
import { CampsiteDetailLocationRowProps } from './CampsiteDetailLocationRowProps'

@Component({
  components: {
    CampsiteDetailLocationRow,
    PuNutshells,
  },
})
class CampsiteDetailCardContent
  extends Vue
  implements CampsiteDetailCardContentProps
{
  @Prop({ required: true })
  campsite: CampsiteDetails

  @Prop({ required: true })
  isDated: boolean

  @Prop({ required: true })
  hasAvailability: boolean

  // Preload check-availability page chunks during idle time so the click is instant
  mounted() {
    if (!this.isDated) {
      requestIdleCallback(() => {
        const router = useRouter()
        void preloadRouteComponents(
          `/campsite/${this.campsite.slug}/check-availability/`,
          router,
        )
      })
    }
  }

  get locationRowProps(): CampsiteDetailLocationRowProps {
    return {
      campsite: this.campsite,
      isDated: this.isDated,
      hasAvailability: this.hasAvailability,
    }
  }
}

export default toNative(CampsiteDetailCardContent)
</script>
<style scoped>
.check-availability-btn {
  font-weight: 400;
  font-size: 1.25em;
  box-shadow: 0 0.275em var(--colour-dark-green);
}

.cs-card-content :deep(.open-status) {
  top: -0.35em;
  position: relative;
}

.cs-card-content :deep(.last-booked) {
  margin-top: 0.3em;
}

.not-available-to-search {
  background: var(--colour-page-background-light);
  padding: 0.6em;
  border-radius: 0.5em;
  margin: 0.75em -0.7em 1em;
}

.not-available-to-search .zero-availability-msg {
  background-color: none;
  border-radius: 0;
  padding: 0;
}

.not-available-to-search .zero-availability-options {
  margin: 1em 0 0;
}

.button-wrapper {
  clear: both;
}

.zero-availability-msg {
  background-color: var(--colour-page-background-light);
  border-radius: 0.4em;
  padding: 0.5em;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--colour-dark-blue);
  font-weight: bold;
  line-height: 1.2;
  font-size: var(--body);
}

.zero-availability-options {
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  justify-content: space-between;
  text-align: left;
  gap: 1em;
  margin: 1em 0 -1em;
  font-size: var(--body);
}

.options {
  display: flex;
  flex-direction: column;
  row-gap: 0.5em;
}

.options a {
  color: var(--colour-dark-blue);
}

.options a:first-of-type {
  text-decoration: none;
}

.zero-availability-options .button {
  font-weight: normal;
  box-shadow: 0 0.275em var(--colour-dark-green);
  padding: 0.5em 0.5em 0.35em;
}
</style>
